import { NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChip } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTree, MatTreeNode, MatTreeNodeDef, MatTreeNodePadding, MatTreeNodeToggle } from '@angular/material/tree';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ELoadStatus } from '../../../../state/state.interface';
import { ESnackbar, SnackbarService } from '../../../../utilities/snackbar.service';
import { IHomeBoard } from '../../../home/home.interface';
import { IItemJoins } from '../../items.interface';
import { FlatNode, IEscalateBoard } from './item-escalete.interface';
import { IItemEscalateState, ItemEscalateStore } from './item-escalete.store';

@Component({
  imports: [
    MatExpansionModule,
    MatDialogContent,
    TranslateModule,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatDialogTitle,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
    MatChip,
    MatIcon,
    NgTemplateOutlet,
    NgIf,
    MatTree,
    MatTreeNode,
    MatTreeNodeDef,
    MatTreeNodePadding,
    MatIconButton,
    MatTreeNodeToggle,
  ],
  providers: [ItemEscalateStore],
  selector: 'app-item-escalate',
  styleUrl: './item-escalate.component.scss',
  templateUrl: './item-escalate.component.html',
})
export class ItemEscalateComponent implements OnInit {
  @ViewChild(MatTree) tree!: MatTree<FlatNode>;
  public boards: IEscalateBoard[] = [];
  public escalatedBoardId: number | null = null;
  public childrenAccessor: (node: IEscalateBoard) => IEscalateBoard[] = (node: IEscalateBoard) => node.children ?? [];
  public hasChild = (_: number, node: IEscalateBoard): boolean => !!node.children && node.children.length > 0;

  constructor(
    public dialogRef: MatDialogRef<ItemEscalateComponent>,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private itemEscalateStore: ItemEscalateStore,
    @Inject(MAT_DIALOG_DATA) public item: IItemJoins | null,
  ) {}

  private transformToTreeStructure(boards: IHomeBoard[]): IEscalateBoard[] {
    const boardMap = new Map<number, any>();
    const parentBoards: IEscalateBoard[] = [];

    boards.forEach((board: IHomeBoard) => {
      boardMap.set(board.id, { ...board, children: [] });
    });

    boards.forEach((board: IHomeBoard) => {
      const parentBoard: IEscalateBoard = boardMap.get(board.parentId!);

      if (board.parentId === null || parentBoard === undefined) {
        parentBoards.push(boardMap.get(board.id)!);
      } else if (parentBoard) {
        parentBoard.children = parentBoard.children ?? [];
        parentBoard.children.push(boardMap.get(board.id)!);
      }
    });

    return parentBoards;
  }

  public ngOnInit(): void {
    this.escalatedBoardId = this.item?.escalatedBoardId ? this.item?.escalatedBoardId : null;

    if (this.item?.boardId) {
      const escalatedBoardId: number = this.item.escalatedBoard ? this.item.escalatedBoardId : this.item?.boardId;
      this.itemEscalateStore.loadBoards({ boardId: this.item?.boardId, escalatedBoardId });
    }

    this.itemEscalateStore
      .select((state: IItemEscalateState) => state.boardsLoading)
      .pipe(filter((value: ELoadStatus): boolean => value === ELoadStatus.success))
      .subscribe(() => {
        this.boards = this.transformToTreeStructure(this.itemEscalateStore.boards());

        setTimeout(() => {
          if (this.tree && this.boards.length > 0) {
            this.tree.expandAll();
          }
        });
      });

    this.itemEscalateStore
      .select((state: IItemEscalateState) => ({
        editItemLoading: state.editItemLoading,
      }))
      .pipe(filter(({ editItemLoading }) => editItemLoading === ELoadStatus.success))
      .subscribe(() => {
        this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
        this.dialogRef.close(true);
      });
  }

  public onSubmit(): void {
    if (this.escalatedBoardId && this.item?.id) {
      const isMainBoard: boolean = this.escalatedBoardId === this.item.boardId;

      this.itemEscalateStore.editItemEscalatedBoardId({
        escalatedBoardId: isMainBoard ? null : this.escalatedBoardId,
        id: this.item?.id,
      });
    }
  }

  public closeDialog(): void {
    this.dialogRef.close(false);
  }
}
